/**
 * 사업자 품질 매트릭스 + 화이트리스트 뱃지 (DESIGN §3.7 · §8.4).
 *
 * **비교 불가 사업자를 숨기지 않는다.** 순위에서 빼되 사유와 함께 보여준다 —
 * "왜 이 사업자는 순위에 없나"에 답할 수 없으면 순위 자체를 믿을 수 없다.
 */

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { pendingNote } from '@/lib/window'
import { VERDICT_LABEL, type Envelope, type OperatorQuality, type Verdict } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import './OperatorMatrix.css'

interface Data {
  operators: OperatorQuality[]
  n_comparable: number
  n_total: number
  /** NA·STALE 임계를 바꿔 보면 몇 곳이 통과하는가 — 게이트는 그대로 둔다 */
  threshold_sensitivity?: {
    current: number
    rows: { threshold: number; n_comparable: number; n_chargers: number }[]
  }
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`
}

const VERDICT_CLASS: Record<Verdict, string> = {
  comparable: 'badge--ok',
  excluded: 'badge--no',
  insufficient_window: 'badge--pending',
}

export function OperatorMatrix() {
  const filters = useFilters()
  const [onlyComparable, setOnlyComparable] = useState(false)
  const q = useQuery({
    queryKey: ['quality/operator_matrix', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<Data>('/quality/operator_matrix', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<Data> | undefined
  const all = env?.data.operators ?? []
  const rows = onlyComparable ? all.filter((o) => o.comparable) : all
  const comparable = env?.data.n_comparable ?? 0
  const pending = all.filter((o) => o.verdict === 'insufficient_window').length
  const windowDays = all[0]?.window_days ?? null
  const sens = env?.data.threshold_sensitivity

  return (
    <ChartFrame
      title="사업자 품질 매트릭스"
      question="어느 사업자의 수치를 비교에 쓸 수 있나"
      meta={env?.meta}
      hideCoverage
      headline={
        pending === all.length && all.length > 0 ? (
          <Headline value={`${pending}`} unit="곳 판정 보류" />
        ) : (
          <Headline
            value={`${comparable} / ${all.length}`}
            unit="비교가능"
            tone={comparable > 0 ? 'up' : 'warn'}
          />
        )
      }
      reading="사업자별로 '이 사업자의 수치를 순위·비교에 써도 되는가'를 판정한 표입니다. 왼쪽 네 열이 판정 근거이고, 맨 오른쪽이 결론과 사유입니다."
      encoding={[
        { role: '충전기', what: '해당 사업자의 충전기 수', detail: '30기 미만이면 비교 대상에서 뺍니다.' },
        {
          role: 'NA율',
          what: '상태를 모르는 시간의 비율',
          detail: '20% 이상이면 비교가능하지 않습니다. 시간 가중이며 집계 창 기준입니다.',
        },
        {
          role: 'STALE율',
          what: '연계가 끊겨 방치된 시간의 비율',
          detail: '20% 이상이면 비교가능하지 않습니다.',
        },
        {
          role: 'updDt 오류',
          what: 'statUpdDt 가 이상한 값인 비율',
          detail: '미래 시각·역행 등입니다. 1% 이상이면 비교가능하지 않습니다.',
        },
        {
          role: '코드',
          what: '실제로 관측된 stat 코드 종류 수',
          detail:
            "3종 미만이거나 '충전대기(2)' 가 없으면 그 사업자는 상태를 성실히 보고하지 않는다고 봅니다. ⚠ 표시가 그 경고입니다.",
        },
        {
          role: 'DQ',
          what: '데이터 품질 점수 (0~1)',
          detail: '0.35(1−NA) + 0.35(1−STALE) + 0.15·코드다양성 + 0.15(1−updDt오류) (§8.4)',
        },
        {
          role: '판정',
          what: '비교가능 · 제외 · 판정 보류',
          detail: '세 값이 서로 다른 진술입니다. 아래 주의사항을 보세요.',
        },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={all.length === 0}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: "'판정 보류'는 '제외'가 아니다.",
          text: `제외는 "기준에 미달한다"이고, 판정 보류는 "관측 창이 28일 기준에 못 미쳐 아직 판단할 수 없다"입니다. ${pendingNote(windowDays)}`,
        },
        {
          label: '비교가능 기준 (§3.7).',
          text: 'NA율<20% ∧ STALE율<20% ∧ 30기 이상 ∧ updDt 오류<1% ∧ 코드 3종 이상(충전대기 포함). 모두 만족해야 통과입니다.',
        },
        {
          label: '비율은 집계 창 기준 시간 가중이다.',
          text: '창을 자르지 않으면 수년 전 statUpdDt 꼬리가 분모를 지배해 모든 사업자가 실패로 나옵니다 (§3.7.1).',
        },
        {
          label: '통과하지 못한 곳도 지우지 않는다.',
          text: '사유를 함께 남깁니다. 사라진 사업자는 "데이터가 없는 사업자"와 구별되지 않습니다.',
        },
      ]}
    >
      <div className="opm__head">
        <p className="opm__summary">
          비교가능 <strong className="num">{comparable.toLocaleString('ko-KR')}</strong> /{' '}
          <span className="num">{all.length.toLocaleString('ko-KR')}</span>곳
          {all.length > 0 && (
            <span className="opm__share"> ({pct(comparable / all.length)})</span>
          )}
          {pending > 0 && (
            <span className="opm__pending">
              {' '}
              · 판정 보류 <span className="num">{pending.toLocaleString('ko-KR')}</span>곳
              {windowDays != null && ` (관측 창 ${windowDays.toFixed(1)}일 / 기준 28일)`}
            </span>
          )}
        </p>
        <label className="opm__toggle">
          <input
            type="checkbox"
            checked={onlyComparable}
            onChange={(e) => setOnlyComparable(e.target.checked)}
          />
          비교가능만
        </label>
      </div>

      {sens && sens.rows.length > 0 && (
        <div className="opm__sens">
          <p className="opm__senslead">
            <strong>임계를 바꾸면 몇 곳이 남나</strong> — NA율·STALE율 기준만 바꾸고 나머지 기준은
            그대로 다시 판정했습니다. 기준은 바꾸지 않았습니다: STALE 은 상태를 <em>모르는</em>{' '}
            시간이라, 느슨하게 하면 모르는 것끼리 비교하게 됩니다.
          </p>
          <table className="opm__senstable">
            <caption className="sr-only">NA·STALE 임계별 비교가능 사업자 수</caption>
            <thead>
              <tr>
                <th scope="row">임계</th>
                {sens.rows.map((r) => (
                  <th
                    key={r.threshold}
                    scope="col"
                    className={r.threshold === sens.current ? 'n is-current' : 'n'}
                  >
                    {Math.round(r.threshold * 100)}%{r.threshold === sens.current && ' (현행)'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">사업자</th>
                {sens.rows.map((r) => (
                  <td key={r.threshold} className="n">
                    {r.n_comparable.toLocaleString('ko-KR')}곳
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">충전기</th>
                {sens.rows.map((r) => (
                  <td key={r.threshold} className="n">
                    {r.n_chargers.toLocaleString('ko-KR')}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="opm__scroll" tabIndex={0} role="region" aria-label="사업자 품질 표">
      <table className="opm">
        <caption className="sr-only">사업자별 데이터 품질과 비교가능 여부</caption>
        <thead>
          <tr>
            <th scope="col">사업자</th>
            <th scope="col" className="n">
              충전기
            </th>
            <th scope="col" className="n">
              NA율
            </th>
            <th scope="col" className="n">
              STALE율
            </th>
            <th scope="col" className="n">
              updDt 오류
            </th>
            <th scope="col" className="n">
              코드
            </th>
            <th scope="col" className="n">
              DQ
            </th>
            <th scope="col">판정</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 60).map((o) => (
            <tr key={o.busi_id} className={o.comparable ? '' : 'is-excluded'}>
              <th scope="row">
                <span className="opm__name">{o.busi_nm ?? o.busi_id}</span>
                <span className="opm__id num">{o.busi_id}</span>
              </th>
              <td className="n">{o.n_chargers.toLocaleString('ko-KR')}</td>
              <td className="n">{pct(o.na_rate)}</td>
              <td className="n">{pct(o.stale_rate)}</td>
              <td className="n">{pct(o.upddt_error_rate)}</td>
              <td className="n">
                {o.distinct_codes}
                {!o.uses_down_codes && (
                  <span className="opm__flag" title="4/5 코드를 쓰지 않아 strict 비교에서 제외">
                    ⚠
                  </span>
                )}
              </td>
              <td className="n">{o.quality.toFixed(3)}</td>
              <td>
                <span
                  className={`badge ${VERDICT_CLASS[o.verdict] ?? 'badge--no'}`}
                  title={o.reasons ?? ''}
                >
                  {VERDICT_LABEL[o.verdict] ?? o.verdict}
                </span>
                {o.reasons && <span className="opm__reason">{o.reasons}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {rows.length > 60 && (
        <p className="opm__more num">
          충전기 수 상위 60곳 표시 (전체 {rows.length.toLocaleString('ko-KR')}곳) · 표 안에서 스크롤
        </p>
      )}
    </ChartFrame>
  )
}
